import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import CIcon from '@coreui/icons-react';
import { cilBell } from '@coreui/icons';
import { getAuth } from 'firebase/auth';
import config from '../utils/EnvironmentSwitcher';
import { buildFhirHeaders } from '../utils/fhirAuth';

// ---- Observation auto-review constants ----
const AUTO_REVIEW_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours
const obsReviewTriggerKey = (uid) => `mie_obs_review_triggered_at_${uid}`;
const obsReviewSeenKey = (uid) => `mie_obs_review_seen_at_${uid}`;

const buildSystemUrl = (baseUrl, suffix) => `${String(baseUrl || '').replace(/\/+$/, '')}/fhir/${suffix}`;
const FHIR_PROXY_BASE = `${String(config.apiUrl || '').replace(/\/+$/, '')}/api/fhir`;
// fhirSystemBase is canonical — FHIR identifier system URIs are permanent labels baked into
// stored resources and do NOT change between dev/prod environments.
const FHIR_SYSTEM_BASE = `${String(config.fhirSystemBase || '').replace(/\/+$/, '')}`;
const readSeen = (uid) => {
  try {
    const raw = localStorage.getItem(`mie_seen_task_toasts_${uid}`);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const persistSeen = (uid, seenSet) => {
  try {
    localStorage.setItem(`mie_seen_task_toasts_${uid}`, JSON.stringify(Array.from(seenSet).slice(-200)));
  } catch {
    // ignore
  }
};

const getTaskMessage = (task) => {
  if (task.status === 'failed') {
    return task.statusReason?.text || 'Scheduled action failed.';
  }
  const output = Array.isArray(task.output) ? task.output : [];
  const dispatch = output.find((o) => o?.type?.text === 'openclaw-dispatch');
  return dispatch?.valueString || task.note?.[0]?.text || 'Scheduled action completed.';
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Resolve a real MiE agent UUID from a FHIR Task.
 * Prefer explicit agent-id identifier. Do NOT treat OpenClaw/note slugs
 * (e.g. agent-no_recent_observations-<timestamp>) as agent IDs.
 */
const getAgentIdFromTask = (task) => {
  const explicit = getIdentifierValue(task, 'agent-id');
  if (explicit && UUID_RE.test(String(explicit))) {
    return String(explicit);
  }

  const notesSystem = buildSystemUrl(FHIR_SYSTEM_BASE, 'notes');
  const identifiers = Array.isArray(task?.identifier) ? task.identifier : [];
  const noteId =
    identifiers.find((i) =>
      i?.value &&
      (i?.system === notesSystem || String(i?.system || '').endsWith('/fhir/notes'))
    )?.value || '';
  const match = String(noteId).match(/^(.+)-\d{10,}$/);
  const candidate = match?.[1] || null;
  // Real agents are UUIDs (see agent.service createAgent). Slugs are scheduler/note keys.
  if (candidate && UUID_RE.test(candidate)) {
    return candidate;
  }
  return null;
};

const getIdentifierValue = (task, suffix) => {
  const identifiers = Array.isArray(task?.identifier) ? task.identifier : [];
  return (
    identifiers.find((i) => i?.value && String(i?.system || '').endsWith(`/fhir/${suffix}`))?.value || null
  );
};

const ScheduledTaskNotifier = ({ authUser, compact = false }) => {
  const navigate = useNavigate();
  const uid = authUser?.uid || authUser?.authUser?.uid || null;
  const startedAtRef = useRef(Date.now());
  const seenRef = useRef(new Set());
  const obsReviewingRef = useRef(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!uid) return;
    seenRef.current = readSeen(uid);
  }, [uid]);

  useEffect(() => {
    if (!uid) return;

    let timer = null;

    const poll = async () => {
      try {
        const userIdSystem = buildSystemUrl(FHIR_SYSTEM_BASE, 'ids');
        const notesSystem = buildSystemUrl(FHIR_SYSTEM_BASE, 'notes');
        const identifier = encodeURIComponent(`${userIdSystem}|${uid}`);
        const url = `${FHIR_PROXY_BASE}/Task?identifier=${identifier}&status=completed,failed&_sort=-_lastUpdated&_count=25`;
        const initialHeaders = await buildFhirHeaders({ 'Content-Type': 'application/fhir+json' }, false);
        if (!initialHeaders.Authorization) return;

        let res = await fetch(url, {
          headers: initialHeaders,
        });
        if (res.status === 401) {
          const refreshHeaders = await buildFhirHeaders({ 'Content-Type': 'application/fhir+json' }, true);
          if (!refreshHeaders.Authorization) return;
          res = await fetch(url, {
            headers: refreshHeaders,
          });
        }
        if (!res.ok) {
          return;
        }
        const bundle = await res.json();
        const entries = Array.isArray(bundle?.entry) ? bundle.entry : [];

        for (const entry of entries) {
          const task = entry?.resource;
          if (!task?.id) continue;
          const taskId = String(task.id);
          const identifiers = Array.isArray(task.identifier) ? task.identifier : [];
          const isScheduledTask = identifiers.some((i) =>
            i?.value &&
            (i?.system === notesSystem || String(i?.system || '').endsWith('/fhir/notes'))
          );
          if (!isScheduledTask) continue;
          if (seenRef.current.has(taskId)) continue;

          const updatedAt = new Date(task.meta?.lastUpdated || task.authoredOn || 0).getTime();
          if (!Number.isFinite(updatedAt) || updatedAt < startedAtRef.current - 5000) continue;

          const status = task.status;
          const title = status === 'failed' ? 'Scheduled Action Failed' : 'Scheduled Action Complete';
          const description = getTaskMessage(task);
          const agentId = getAgentIdFromTask(task);
          const agentTaskId = getIdentifierValue(task, 'agent-task-id');
          const conversationId = getIdentifierValue(task, 'agent-conversation-id');
          const notificationItem = {
            id: `task-${taskId}`,
            taskId,
            agentId,
            agentTaskId,
            conversationId,
            status,
            title,
            taskTitle: task.description || 'Scheduled task',
            description,
            updatedAt: task.meta?.lastUpdated || task.authoredOn || new Date().toISOString(),
          };

          setNotifications((prev) => {
            const next = [notificationItem, ...prev.filter((n) => n.id !== notificationItem.id)];
            return next.slice(0, 100);
          });
          setUnreadCount((prev) => prev + 1);

          if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
            const browserNotification = new Notification(title, {
              body: `${notificationItem.taskTitle}: ${description}`,
              tag: `scheduled-task-${taskId}`,
            });
            browserNotification.onclick = () => {
              window.focus();
              setPanelOpen(true);
            };
          }

          toast(
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ fontWeight: 700, letterSpacing: '0.01em' }}>{title}</div>
              <div style={{ opacity: 0.9, fontSize: 13 }}>{notificationItem.taskTitle}</div>
              <div style={{ opacity: 0.75, fontSize: 12, maxHeight: 48, overflow: 'hidden' }}>{description}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={() => setPanelOpen(true)}
                  style={{
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'transparent',
                    color: 'inherit',
                    borderRadius: 8,
                    padding: '4px 8px',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  View Task
                </button>
                {agentId && (
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/agents/${agentId}`)}
                    style={{
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'transparent',
                      color: 'inherit',
                      borderRadius: 8,
                      padding: '4px 8px',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    Open Agent
                  </button>
                )}
              </div>
            </div>,
            {
              toastId: `scheduled-task-${taskId}`,
              type: status === 'failed' ? 'error' : 'success',
              autoClose: 9000,
              closeOnClick: true,
            }
          );

          seenRef.current.add(taskId);
        }

        persistSeen(uid, seenRef.current);
      } catch {
        // ignore poll errors; next interval will retry
      }
    };

    poll();
    timer = setInterval(poll, 20000);

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [uid]);

  // ---- Periodic AI observation review (auto-triggers every 6 hours) ----
  useEffect(() => {
    if (!uid) return;

    let cancelled = false;

    const showInsightToast = (summary, lastRunAt) => {
      const summaryText =
        typeof summary === 'string'
          ? summary
          : (summary && typeof summary === 'object' && Number.isFinite(Number(summary.total)))
            ? `Processed ${Number(summary.total)} observations in the latest review.`
            : '';
      toast.info(
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{ fontWeight: 700, letterSpacing: '0.01em' }}>AI Health Review Complete</div>
          {summaryText && (
            <div style={{ opacity: 0.85, fontSize: 12 }}>
              {summaryText.length > 120 ? `${summaryText.slice(0, 120)}...` : summaryText}
            </div>
          )}
          <button
            type="button"
            onClick={() => navigate('/dashboard/profile/agent')}
            style={{
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: 'inherit',
              borderRadius: 8,
              padding: '4px 8px',
              fontSize: 12,
              cursor: 'pointer',
              marginTop: 4,
            }}
          >
            View Profile
          </button>
        </div>,
        { toastId: `obs-review-${lastRunAt}`, autoClose: 10000 }
      );
    };

    const runObsReview = async () => {
      if (obsReviewingRef.current || cancelled) return;

      // Throttle: skip if we already triggered a review recently in this browser
      const lastTriggeredAt = Number(localStorage.getItem(obsReviewTriggerKey(uid)) || 0);
      if (Date.now() - lastTriggeredAt < AUTO_REVIEW_INTERVAL_MS) return;

      try {
        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        if (!token || cancelled) return;

        // Check profile's own lastRunAt — another device may have already run it
        const insightsRes = await fetch(`${config.apiUrl}/api/profile/insights`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!insightsRes.ok || cancelled) return;
        const insightsData = await insightsRes.json();

        const profileLastRunAt = insightsData?.observationLearning?.lastRunAt;
        const profileLastRunMs = profileLastRunAt ? new Date(profileLastRunAt).getTime() : 0;

        // Profile already reviewed recently (another device/tab ran it) — just notify if new
        if (Date.now() - profileLastRunMs < AUTO_REVIEW_INTERVAL_MS) {
          const lastSeen = localStorage.getItem(obsReviewSeenKey(uid));
          if (profileLastRunAt && profileLastRunAt !== lastSeen) {
            localStorage.setItem(obsReviewSeenKey(uid), profileLastRunAt);
            showInsightToast(insightsData?.observationLearning?.summary, profileLastRunAt);
          }
          return;
        }

        // Run the review
        obsReviewingRef.current = true;
        localStorage.setItem(obsReviewTriggerKey(uid), String(Date.now()));

        const reviewRes = await fetch(`${config.apiUrl}/api/profile/agent-md/learn-observations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ lookbackDays: 7, createReminders: true }),
        });
        if (!reviewRes.ok || cancelled) return;

        // Re-fetch insights to get the updated summary
        const updatedRes = await fetch(`${config.apiUrl}/api/profile/insights`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!updatedRes.ok || cancelled) return;
        const updatedInsights = await updatedRes.json();

        const newLastRunAt = updatedInsights?.observationLearning?.lastRunAt;
        const lastSeen = localStorage.getItem(obsReviewSeenKey(uid));
        if (newLastRunAt && newLastRunAt !== lastSeen) {
          localStorage.setItem(obsReviewSeenKey(uid), newLastRunAt);
          showInsightToast(updatedInsights?.observationLearning?.summary, newLastRunAt);
        }
      } catch {
        // silent — background task, never surface errors to user
      } finally {
        obsReviewingRef.current = false;
      }
    };

    runObsReview();
    const timer = setInterval(runObsReview, AUTO_REVIEW_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      style={
        compact
          ? { position: 'relative', marginRight: 10 }
          : { position: 'fixed', right: 16, bottom: 16, zIndex: 1300 }
      }
    >
      <button
        type="button"
        onClick={() => {
          setPanelOpen((v) => !v);
          setUnreadCount(0);
        }}
        style={{
          border: '1px solid rgba(255,255,255,0.25)',
          background: 'rgba(10,20,30,0.85)',
          color: '#fff',
          borderRadius: 12,
          padding: '8px 12px',
          cursor: 'pointer',
          fontWeight: 600,
          minWidth: 66,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        {compact ? (
          <>
            <CIcon icon={cilBell} />
            {unreadCount > 0 && (
              <span
                style={{
                  background: '#ef4444',
                  color: '#fff',
                  borderRadius: 999,
                  padding: '0 6px',
                  fontSize: 11,
                  lineHeight: '18px',
                  minWidth: 18,
                  textAlign: 'center',
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </>
        ) : (
          `Alerts ${unreadCount > 0 ? `(${unreadCount})` : ''}`
        )}
      </button>

      {panelOpen && (
        <div
          style={{
            marginTop: compact ? 6 : 8,
            width: 360,
            maxHeight: 420,
            overflowY: 'auto',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(7,13,20,0.94)',
            color: '#fff',
            padding: 10,
            boxShadow: '0 12px 36px rgba(0,0,0,0.35)',
            position: compact ? 'absolute' : 'static',
            right: compact ? 0 : 'auto',
            top: compact ? 40 : 'auto',
            zIndex: 1500,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Scheduled Notifications</div>
          {notifications.length === 0 && (
            <div style={{ opacity: 0.75, fontSize: 13 }}>No notifications yet.</div>
          )}
          {notifications.map((item) => (
            <div
              key={item.id}
              style={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                padding: 8,
                marginBottom: 8,
                background: item.status === 'failed' ? 'rgba(140,20,20,0.2)' : 'rgba(20,90,40,0.16)',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{item.title}</div>
              <div style={{ fontSize: 12, opacity: 0.92 }}>{item.taskTitle}</div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>{item.description}</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => toast.info(`Task ${item.taskId}`)}
                  style={{ borderRadius: 8, border: 0, padding: '4px 8px', cursor: 'pointer' }}
                >
                  Task ID
                </button>
                {item.agentId && (
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/agents/${item.agentId}`)}
                    style={{ borderRadius: 8, border: 0, padding: '4px 8px', cursor: 'pointer' }}
                  >
                    Open Agent
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScheduledTaskNotifier;

